import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import JAutoCompleteNotifier from './JAutoCompleteNotifier';
import './JAutoComplete.css';

/**
 * JAutoComplete — reusable searchable dropdown without external dependencies.
 *
 * Features:
 * - Type to filter items
 * - Enter → selects first filtered item (or keyboard-focused item)
 *           fires enterPressed event via JAutoCompleteNotifier
 * - ↑↓ keyboard navigation
 * - Escape → closes dropdown
 * - Tab / blur → closes dropdown
 * - × clear button
 *
 * Events via JAutoCompleteNotifier(id):
 *   { event: 'selected',      value }  — item committed (click or Enter)
 *   { event: 'enterPressed',  value }  — committed via Enter key specifically
 *   { event: 'cleared'              }  — × pressed
 */
export default function JAutoComplete({ id, items, value, onChange, placeholder = 'Zoeken...', className = '' }) {
    const [query,      setQuery]      = useState(value ?? '');
    const [open,       setOpen]       = useState(false);
    const [focusIndex, setFocusIndex] = useState(0);
    const wrapRef    = useRef(null);
    const blurTimer  = useRef(null);

    useEffect(() => { setQuery(value ?? ''); }, [value]);

    // Cleanup blur timer on unmount
    useEffect(() => () => clearTimeout(blurTimer.current), []);

    const filtered = useMemo(() =>
        items.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
        [items, query]
    );

    useEffect(() => { setFocusIndex(0); }, [filtered]);

    const selectByClick = useCallback((item) => {
        setQuery(item);
        setOpen(false);
        setFocusIndex(0);
        onChange(item);
        JAutoCompleteNotifier.notify(id, 'selected', item);
    }, [id, onChange]);

    const selectByEnter = useCallback((item) => {
        setQuery(item);
        setOpen(false);
        setFocusIndex(0);
        onChange(item);
        JAutoCompleteNotifier.notify(id, 'selected', item);
        JAutoCompleteNotifier.notify(id, 'enterPressed', item);
    }, [id, onChange]);

    const handleInput = (e) => {
        setQuery(e.target.value);
        setOpen(true);
        if (value && !items.includes(e.target.value)) onChange('');
    };

    const handleClear = () => {
        setQuery('');
        setOpen(false);
        onChange('');
        JAutoCompleteNotifier.notify(id, 'cleared');
    };

    const handleKeyDown = (e) => {
        if (!open && e.key !== 'Escape') setOpen(true);
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setFocusIndex((i) => Math.min(i + 1, filtered.length - 1));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setFocusIndex((i) => Math.max(i - 1, 0));
                break;
            case 'Enter':
                e.preventDefault();
                if (filtered.length > 0) selectByEnter(filtered[focusIndex] ?? filtered[0]);
                break;
            case 'Escape':
                setOpen(false);
                break;
            default:
                break;
        }
    };

    // Sluit dropdown bij blur (Tab naar volgend element).
    // Delay zodat mousedown op een dropdown item nog kan afvuren vóór close.
    const handleBlur = () => {
        blurTimer.current = setTimeout(() => setOpen(false), 150);
    };

    // Als focus terugkeert naar de input (bijv. klik op × knop) — cancel de close
    const handleFocus = () => {
        clearTimeout(blurTimer.current);
        setOpen(true);
    };

    return (
        <div className={`jac-root ${className}`} ref={wrapRef}>
            <div className="jac-input-wrap">
                <input
                    className="jac-input"
                    type="text"
                    value={query}
                    placeholder={placeholder}
                    onChange={handleInput}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    spellCheck={false}
                />
                {query && (
                    <button className="jac-clear-btn" tabIndex={-1} onMouseDown={(e) => { e.preventDefault(); handleClear(); }}>
                        ×
                    </button>
                )}
            </div>

            {open && (
                <div className="jac-dropdown">
                    {filtered.length === 0
                        ? <div className="jac-empty">Geen resultaten</div>
                        : filtered.map((item, i) => (
                            <div
                                key={item}
                                className={[
                                    'jac-item',
                                    i === focusIndex ? 'jac-item--focused'  : '',
                                    item === value   ? 'jac-item--selected' : '',
                                ].join(' ')}
                                onMouseDown={(e) => { e.preventDefault(); selectByClick(item); }}
                            >
                                {item}
                            </div>
                        ))
                    }
                </div>
            )}
        </div>
    );
}
